import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import {
	useCreateAccount,
	useGetAccounts,
	useUpdateAccount,
} from "../../hooks/useAccounts";
import { Account, AccountRequest } from "../../domain/account.interface";
import { useUserStore } from "../../stores/user.store";
import AccountForm, { EMPTY_ACCOUNT } from "./AccountForm";

// The backoffice BaseCreateDto/BaseUpdateDto require non-empty
// description (`@Length(3, 3000)`) and a valid ObjectId in
// createdBy/updatedBy. The Accounts form allows description to be
// blank, so drop it when empty rather than send "" and trip
// validation. createdBy/updatedBy come from the logged-in user.
const sanitizeAccountPayload = <T extends Partial<AccountRequest>>(
	data: T,
): T => {
	const cleaned: T = { ...data };
	if (!cleaned.description || cleaned.description.trim().length === 0) {
		delete cleaned.description;
	}
	return cleaned;
};

const AccountsComponent: FC = () => {
	const [createOpen, setCreateOpen] = useState(false);
	const [editingAccount, setEditingAccount] = useState<Account | null>(null);

	const { data: accounts, isLoading, error, refetch } = useGetAccounts();
	const createMutation = useCreateAccount();
	const updateMutation = useUpdateAccount();
	const userInfo = useUserStore((s) => s.userInfo);
	const currentUserId = userInfo?.id;

	const handleCreate = async (data: AccountRequest) => {
		if (!currentUserId) {
			// eslint-disable-next-line no-console
			console.error("Cannot create account: no logged-in user id");
			return;
		}
		try {
			await createMutation.mutateAsync({
				...sanitizeAccountPayload(data),
				createdBy: currentUserId,
			} as AccountRequest);
			setCreateOpen(false);
			refetch();
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("Error creating account:", err);
		}
	};

	const handleUpdate = async (data: AccountRequest) => {
		if (!editingAccount) return;
		if (!currentUserId) {
			// eslint-disable-next-line no-console
			console.error("Cannot update account: no logged-in user id");
			return;
		}
		try {
			await updateMutation.mutateAsync({
				accountId: editingAccount._id,
				accountData: {
					...sanitizeAccountPayload(data),
					updatedBy: currentUserId,
				},
			});
			setEditingAccount(null);
			refetch();
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("Error updating account:", err);
		}
	};

	const editInitialValues: AccountRequest | undefined = editingAccount
		? {
				name: editingAccount.name,
				status: editingAccount.status,
				description: editingAccount.description ?? "",
				auth0: { ...editingAccount.auth0 },
				qlik: { ...editingAccount.qlik },
				tremendous: { ...editingAccount.tremendous },
			}
		: undefined;

	if (isLoading) return <Typography>Loading accounts...</Typography>;
	if (error)
		return <Typography color="error">Error loading accounts</Typography>;

	return (
		<Box sx={{ padding: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Account Management
				</Typography>
				<Button variant="contained" onClick={() => setCreateOpen(true)}>
					Create New Account
				</Button>
			</Box>

			<Grid container spacing={3}>
				{accounts?.map((account) => (
					<Grid item xs={12} md={6} lg={4} key={account._id}>
						<Card>
							<CardContent>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									mb={2}
								>
									<Typography variant="h6">{account.name}</Typography>
									<Box display="flex" alignItems="center" gap={1}>
										<Chip
											label={account.status}
											color={
												account.status === "ACTIVE" ? "success" : "default"
											}
										/>
										<Button
											size="small"
											startIcon={<EditIcon />}
											onClick={() => setEditingAccount(account)}
										>
											Edit
										</Button>
									</Box>
								</Box>
								<Typography variant="body2" color="text.secondary" paragraph>
									{account.description || "No description"}
								</Typography>
								<Typography variant="caption" display="block" gutterBottom>
									Created:{" "}
									{new Date(account.createdAt || "").toLocaleDateString()}
								</Typography>
								<Typography variant="caption" display="block">
									ID: {account._id}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>

			<Dialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Create New Account</DialogTitle>
				<DialogContent dividers>
					<AccountForm
						initialValues={EMPTY_ACCOUNT}
						submitting={createMutation.isPending}
						submitLabel="Create Account"
						onSubmit={handleCreate}
						onCancel={() => setCreateOpen(false)}
					/>
				</DialogContent>
			</Dialog>

			<Dialog
				open={editingAccount !== null}
				onClose={() => setEditingAccount(null)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Edit Account — {editingAccount?.name}</DialogTitle>
				<DialogContent dividers>
					{editInitialValues ? (
						<AccountForm
							initialValues={editInitialValues}
							submitting={updateMutation.isPending}
							submitLabel="Save Changes"
							onSubmit={handleUpdate}
							onCancel={() => setEditingAccount(null)}
						/>
					) : null}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default AccountsComponent;
